/**
 * Visit strip: real storefront crop, NAP, hours, and a map.
 * This is how people choose a bakery — show the shop, not a stock interior.
 */

import { motion } from 'framer-motion';
import { ClockIcon, MapPinIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { BAKERY_LOCAL } from '../../utils/bakeryLocal';
import { PREVIEW_VISIT_IMAGE } from './previewAssets';
import type { PreviewContact } from './usePreviewHomeData';

export default function PreviewVisitStrip({ contact }: { contact: PreviewContact }) {
  const tel = contact.phone.replace(/\D/g, '');

  return (
    <section className="preview-paper section-padding">
      <div className="container-custom grid items-center gap-10 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="overflow-hidden rounded-3xl shadow-xl"
        >
          <img
            src={PREVIEW_VISIT_IMAGE}
            alt="Nanny B's Bakery storefront windows at Commerce Park in Barrie"
            className="h-full w-full object-cover"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-700">Visit the shop</p>
          <h2 className="mt-2 font-display text-3xl font-bold text-gray-900 md:text-4xl">
            Come see us at Commerce Park
          </h2>
          {/* Do not say nut-friendly here. The shop is not nut-friendly. */}
          <p className="mt-4 max-w-lg text-gray-600">
            Family bakery in Barrie. Gluten-free options available. Custom cakes need 72 hours notice.
          </p>

          <ul className="mt-8 space-y-4 text-gray-800">
            <li className="flex gap-3">
              <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
              <a
                href={BAKERY_LOCAL.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-700"
              >
                {contact.address}
              </a>
            </li>
            <li className="flex gap-3">
              <ClockIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
              <span>{contact.hours}</span>
            </li>
            <li className="flex gap-3">
              <PhoneIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
              <a href={`tel:${tel}`} className="hover:text-primary-700">
                {contact.phone}
              </a>
            </li>
          </ul>

          <div className="mt-8 overflow-hidden rounded-2xl border border-primary-100 shadow-sm">
            <iframe
              title="Map to Nanny B's Bakery in Barrie"
              src={BAKERY_LOCAL.mapsEmbedUrl}
              className="h-48 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
